import { Box, Typography, Chip, SxProps, Theme } from "@mui/material";
import { SelectionItem } from "./SelectionColumn";
import React from "react";

export interface QualityDestinationColumnProps {
  // Quality props
  qualities: SelectionItem[];
  selectedQualities: string[];
  onQualityChange: (value: string) => void;

  // Destination props
  destinations: Array<{ value: string | null; label: string }>;
  selectedDestinations: (string | null)[];
  onDestinationChange: (value: string | null) => void;

  // Style customization
  customWidth?: string;

  // Behavior customization
  showTitleDivider?: boolean;

  // Optional additional content (e.g., Billet Details Form)
  children?: React.ReactNode;
}

export const QualityDestinationColumn = ({
  qualities,
  selectedQualities,
  onQualityChange,
  destinations,
  selectedDestinations,
  onDestinationChange,
  customWidth,
  showTitleDivider = false,
  children,
}: QualityDestinationColumnProps) => {
  const handleQualityClick = (value: string) => {
    onQualityChange(value);
  };

  const handleDestinationClick = (value: string | null) => {
    onDestinationChange(value);
  };

  // Prepare quality chips with selection status and sort
  const qualityChips = qualities.map((quality, index) => {
    const qualityValue = quality.Value || quality.value || "";
    const qualityTitle = quality.Title || quality.label || qualityValue;
    const isSelected = selectedQualities.includes(qualityValue);
    return {
      value: qualityValue,
      label: qualityTitle,
      isSelected,
      originalIndex: index,
    };
  });

  // Sort quality chips: selected first
  const sortedQualityChips = qualityChips.sort((a, b) => {
    if (a.isSelected && !b.isSelected) return -1;
    if (!a.isSelected && b.isSelected) return 1;
    return a.originalIndex - b.originalIndex;
  });

  const selectedQualityChips = sortedQualityChips.filter((c) => c.isSelected);
  const unselectedQualityChips = sortedQualityChips.filter(
    (c) => !c.isSelected,
  );

  // Prepare destination chips with selection status and sort
  const destinationChips = destinations.map((destination, index) => {
    const destinationValue = destination.value; // Keep null as null
    const destinationLabel = destination.label || destinationValue || "Unknown";
    const isSelected = selectedDestinations.includes(destinationValue);
    return {
      value: destinationValue,
      label: destinationLabel,
      isSelected,
      originalIndex: index,
    };
  });

  // Sort destination chips: selected first
  const sortedDestinationChips = destinationChips.sort((a, b) => {
    if (a.isSelected && !b.isSelected) return -1;
    if (!a.isSelected && b.isSelected) return 1;
    return a.originalIndex - b.originalIndex;
  });

  const selectedDestinationChips = sortedDestinationChips.filter(
    (c) => c.isSelected,
  );
  const unselectedDestinationChips = sortedDestinationChips.filter(
    (c) => !c.isSelected,
  );

  const containerSx = {
    width: customWidth || "15%",
    height: "100%",
    marginLeft: "0.5%",
    marginRight: "0.5%",
    background: (theme: Theme) => theme.palette.billetYard.backgrounds.gradient,
    border: (theme: Theme) =>
      `1px solid ${theme.palette.billetYard.borders.light}`,
    borderRadius: "12px",
    padding: "14px",
    overflowY: "auto",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  };

  const headerSx = {
    fontSize: "20px",
    fontWeight: 700,
    color: (theme: Theme) => theme.palette.text.primary,
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: (theme: Theme) =>
      `2px solid ${theme.palette.billetYard.borders.light}`,
    textAlign: "center",
    lineHeight: 1.2,
  };

  const chipContainerSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    padding: "8px",
    justifyContent: "center",
  };

  const baseChipSx = {
    fontSize: "16px",
    height: "48px",
    minWidth: "80px",
    cursor: "pointer",
    padding: "12px 16px",
    borderRadius: "24px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
    },
  };

  const selectedChipSx = {
    ...baseChipSx,
    backgroundColor: (theme: Theme) =>
      theme.palette.billetYard.selected.background,
    color: (theme: Theme) => theme.palette.text.primary,
    fontWeight: 600,
    border: (theme: Theme) =>
      `1px solid ${theme.palette.billetYard.selected.border}`,
    "&:hover": {
      backgroundColor: (theme: Theme) =>
        theme.palette.billetYard.selected.hover,
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
    },
  };

  const unselectedChipSx = {
    ...baseChipSx,
    backgroundColor: "transparent",
    color: "inherit",
    fontWeight: 400,
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    "&:hover": {
      backgroundColor: (theme: Theme) => theme.palette.action.hover,
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
    },
  };

  return (
    <Box sx={containerSx}>
      {/* Quality Section */}
      <Box>
        <Typography sx={headerSx}>Quality</Typography>

        {/* Divider line below title - only show when there are selected items */}
        {showTitleDivider && selectedQualityChips.length > 0 && (
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: (theme) =>
                theme.palette.billetYard.borders.light,
              marginBottom: "8px",
            }}
          />
        )}

        <Box sx={chipContainerSx}>
          {/* Render selected quality chips first */}
          {selectedQualityChips.map((chip) => (
            <Chip
              key={`selected-${chip.value}`}
              label={chip.label}
              onClick={() => handleQualityClick(chip.value)}
              variant="outlined"
              sx={selectedChipSx}
            />
          ))}

          {/* Visual separator between selected and unselected */}
          {selectedQualityChips.length > 0 &&
            unselectedQualityChips.length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: (theme) =>
                    theme.palette.billetYard.borders.light,
                  margin: "8px 0",
                }}
              />
            )}

          {/* Render unselected quality chips */}
          {unselectedQualityChips.map((chip) => (
            <Chip
              key={`unselected-${chip.value}`}
              label={chip.label}
              onClick={() => handleQualityClick(chip.value)}
              variant="outlined"
              sx={unselectedChipSx}
            />
          ))}
        </Box>
      </Box>

      {/* Destination Section */}
      <Box sx={{ marginTop: "16px" }}>
        <Typography sx={headerSx}>Destination</Typography>

        {/* Divider line below title - only show when there are selected items */}
        {showTitleDivider && selectedDestinationChips.length > 0 && (
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: (theme) =>
                theme.palette.billetYard.borders.light,
              marginBottom: "8px",
            }}
          />
        )}

        <Box sx={chipContainerSx}>
          {/* Render selected destination chips first */}
          {selectedDestinationChips.map((chip) => (
            <Chip
              key={`selected-${chip.value || "unknown"}`}
              label={chip.label}
              onClick={() => handleDestinationClick(chip.value)}
              variant="outlined"
              sx={selectedChipSx}
            />
          ))}

          {/* Visual separator between selected and unselected */}
          {selectedDestinationChips.length > 0 &&
            unselectedDestinationChips.length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: (theme) =>
                    theme.palette.billetYard.borders.light,
                  margin: "8px 0",
                }}
              />
            )}

          {/* Render unselected destination chips */}
          {unselectedDestinationChips.map((chip) => (
            <Chip
              key={`unselected-${chip.value || "unknown"}`}
              label={chip.label}
              onClick={() => handleDestinationClick(chip.value)}
              variant="outlined"
              sx={unselectedChipSx}
            />
          ))}
        </Box>
      </Box>

      {/* Optional additional content (e.g., Billet Details Form) */}
      {children}
    </Box>
  );
};
